import React, { createContext, useContext, useMemo, useState, useEffect } from 'react';
import { useBreakpoint } from '../hooks/useBreakpoint';
import { useMediaQuery } from '../hooks/useMediaQuery';

/**
 * Responsive Context — viewport tiers from useBreakpoint.
 * `isMobile` is true when width < 768px (aligned with md: / tablet: tier).
 */
const ResponsiveContext = createContext(null);

/**
 * Hook to detect touch device
 */
function useTouchDevice() {
  const [isTouchDevice, setIsTouchDevice] = useState(false);

  useEffect(() => {
    if (typeof window === 'undefined') return;
    
    const checkTouch = () => {
      setIsTouchDevice(
        'ontouchstart' in window ||
        navigator.maxTouchPoints > 0 ||
        window.matchMedia('(pointer: coarse)').matches
      );
    };

    checkTouch();
    
    // Re-check on resize (for devices that can switch modes)
    window.addEventListener('resize', checkTouch);
    return () => window.removeEventListener('resize', checkTouch);
  }, []);

  return isTouchDevice;
}

/**
 * Hook to detect orientation
 */
function useOrientation() {
  const isPortrait = useMediaQuery('(orientation: portrait)');
  return isPortrait ? 'portrait' : 'landscape';
}

/**
 * ResponsiveProvider - wraps the application to provide responsive context
 */
export function ResponsiveProvider({ children }) {
  const breakpointState = useBreakpoint();
  const isTouchDevice = useTouchDevice();
  const orientation = useOrientation();

  const value = useMemo(() => ({
    // From useBreakpoint
    ...breakpointState,
    // Additional context
    isTouchDevice,
    orientation,
  }), [breakpointState, isTouchDevice, orientation]);

  return (
    <ResponsiveContext.Provider value={value}>
      {children}
    </ResponsiveContext.Provider>
  );
}

/**
 * Hook to access responsive context
 * @returns {Object} Responsive context value
 */
export function useResponsive() {
  const context = useContext(ResponsiveContext);
  
  if (context === null) {
    throw new Error('useResponsive must be used within a ResponsiveProvider');
  }
  
  return context;
}

export default ResponsiveContext;
